import React from 'react';
import { Link } from 'react-router-dom';

/**
 * 規約・法令表記ページ共通のレイアウト。
 * 読み物としての可読性を優先し、本文幅を絞っている。
 */
export const LegalLayout: React.FC<{
  title: string;
  eyebrow: string;
  updatedAt: string;
  lead?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, eyebrow, updatedAt, lead, children }) => (
  <>
    {/* ページヘッダー */}
    <div className="border-b border-line bg-surface-muted">
      <div className="container-content py-12 sm:py-16">
        <nav aria-label="パンくず" className="text-small text-body-soft">
          <Link to="/" className="transition-colors hover:text-ink-900">
            トップ
          </Link>
          <span className="mx-2 text-body-faint">/</span>
          <span className="text-ink-900">{title}</span>
        </nav>

        <p className="eyebrow mt-6">{eyebrow}</p>
        <h1 className="text-h2 mt-3 text-ink-900">{title}</h1>
        {lead && <p className="text-lead mt-4 max-w-prose text-body-soft">{lead}</p>}
        <p className="mt-6 text-small text-body-faint">制定・最終改定日：{updatedAt}</p>
      </div>
    </div>

    {/* 本文 */}
    <div className="container-content py-12 sm:py-16">
      <div className="max-w-prose">{children}</div>

      <div className="mt-16 max-w-prose border-t border-line pt-8">
        <Link to="/" className="btn btn-md btn-secondary">
          ← トップページへ戻る
        </Link>
      </div>
    </div>
  </>
);

/** 条文ブロック（プライバシーポリシー用） */
export const Article: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-10">
    <h2 className="text-h3 border-l-4 border-brand-500 pl-4 text-ink-900">{title}</h2>
    <div className="mt-4 space-y-4 leading-relaxed text-body-soft">{children}</div>
  </section>
);

/** 定義リスト（特定商取引法表記用） */
export const DefinitionTable: React.FC<{
  rows: Array<{ label: string; value: React.ReactNode }>;
}> = ({ rows }) => (
  <dl className="overflow-hidden rounded-card border border-line">
    {rows.map((row, i) => (
      <div
        key={row.label}
        className={`grid gap-1 px-5 py-5 sm:grid-cols-[13rem_1fr] sm:gap-6 sm:px-7 ${
          i % 2 === 1 ? 'bg-surface-muted' : 'bg-white'
        } ${i > 0 ? 'border-t border-line' : ''}`}
      >
        <dt className="text-small font-bold text-ink-900">{row.label}</dt>
        <dd className="text-small leading-relaxed text-body-soft">{row.value}</dd>
      </div>
    ))}
  </dl>
);

/** 箇条書き */
export const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
        <span className="leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);
